import { TItemChannel } from "../model"
import { idgen } from "./id"

const L = console.log
const E = console.error
const W = console.warn

// PRIMITIVE abstraction of a MIDI note
export interface IMidiNote {
  channel: number
  // MIDI note number
  note: number
  // SPN
  pitch: {
    spn: string
    note: string
    octave: number
  }
}

// NOTE names
export const MIDI_NOTES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B']
export const MIDI_MAX_OCTAVE = Math.ceil(128/12)
export const MIDI_OCTAVES = Array(MIDI_MAX_OCTAVE).fill(1).map((_,octave) => octave - 1)
export const MIDI_CHANS = Array(16).fill(1).map((_,channel) => channel + 1)

export type TMidiPort = {id: string, name: string}
export type TMidiPortRec = Record<string,TMidiPort>

export class MidiNote {
    // instance from raw MIDI note
    static fromNumber ({note:$note,channel}:{note:number,channel:number}) : IMidiNote | undefined  {
        // ENSURE MIDI note number is within valid range
        if ($note < 0 || $note > 127) return
        // CHANNEL out of range (1-16)
        if (channel < 1 || channel > 16) return
        const octave = Math.floor($note / 12) - 1
        const note = MIDI_NOTES[$note % 12]
        return {
            channel,
            note: $note,
            pitch: {
                spn: `${note}${octave}`,
                note,
                octave,
            }
        }
    }

    // instance from CHANNEL + SPN
    static fromString ({channel,spn}:{channel:number,spn:string}) : IMidiNote | undefined  {
        spn = spn.trim().toUpperCase()
        const m = spn.match(/^(\D+)(\d+)$/)
        if (!m) return
        const note = m[1]
        // NOTE portion unrecognized
        if (!MIDI_NOTES.includes(note)) return
        const octave = +m[2]
        // OCTAVE out of ranve
        if (octave > MIDI_MAX_OCTAVE || octave < -1) return
        const note_index = MIDI_NOTES.findIndex(n => note === n)
        // NOTE out of range for OCTAVE (> G9)
        if (octave === MIDI_MAX_OCTAVE && note_index > 7) return
        // CHANNEL out of range (1-16)
        if (channel < 1 || channel > 16) return
        return {
            channel,
            note: (octave + 1) * 12 + note_index,
            pitch: {
                spn: `${note}${octave}`,
                note,
                octave,
            }
        }
    }

    static equal (a?: IMidiNote, b?:IMidiNote) : boolean {
      if (!a || !b) return false
      return a.channel === b.channel && a.note === b.note
    }

    static compare (a?: IMidiNote, b?:IMidiNote) : number {
      if (MidiNote.equal(a,b)) return 0
      if (!a && !b) return 0
      if (!a) return 1
      if (!b) return -1
      if (a.channel < b.channel) return -1
      if (b.channel < a.channel) return 1
      if (a.note < b.note) return -1
      return 1 // b.note < a.note
    }

    // SERIALIZE to FULLY QUALIFIED NOTE
    static toString (note: IMidiNote) : string {
        const {pitch,channel} = note
        return `CH ${channel} - ${note.note}/${pitch.note}${pitch.octave}`
    }
}

// MIDI NOTE event
export interface IMidiNoteEvent extends IMidiNote  {
  on?: true | 'hard'
  off?: true | 'synth'
  velocity: number
  time: number
  port?: {
    id: string,
    name?: string,
  }
}

// MIDI NOTE event listener
export type TMidiNoteObserver = (event: IMidiNoteEvent) => void

// MIDI "learnable" entity
export type TMidiSinkChain = {
  chain: string
}

export type TMidiSinkNode = {
  chain: string
  node: string
  channel?: TItemChannel
}

export type TMidiSinkGroup = {
  chain: string
  group: string
}

export type TMidiSinkSession = {
  session: string
}

export type TMidiSinkMute = 'mute'

// MIDI "learnable" entity
export type TMidiSink = 
  TMidiSinkChain
  | TMidiSinkNode
  | TMidiSinkGroup
  | TMidiSinkMute
  | TMidiSinkSession

export type TMidiLearnState = 'eligible' | 'learning'

export class MidiSink {
  static chain (self: TMidiSink) : TMidiSinkChain | undefined {
    return self['chain'] ? self as TMidiSinkGroup : undefined
  }

  static group (self: TMidiSink) : TMidiSinkGroup | undefined {
    return self['group'] ? self as TMidiSinkGroup : undefined
  }

  static node (self: TMidiSink) : TMidiSinkNode | undefined {
    return self['node'] ? self as TMidiSinkNode : undefined
  }

  static mute (self: TMidiSink) : TMidiSinkMute | undefined {
    return self === 'mute' ? self as TMidiSinkMute : undefined
  }

  static session (self: TMidiSink) : TMidiSinkSession | undefined {
    return self['session'] ? self as TMidiSinkSession : undefined
  }

  static exclusive (self: TMidiSink) : boolean {
    return !!this.session(self) || !!this.mute(self)
  }

  static equal (a: TMidiSink, b: TMidiSink) : boolean {
    if (a['chain']) {
      if (a['chain'] !== b['chain']) {
        return false
      }
      if (a['node'] && a['node'] !== b['node']) {
        return false
      }
      if (a['group'] && a['group'] !== b['group']) {
        return false
      }
      if (b['node'] && a['node'] !== b['node']) {
        return false
      }
      if (b['group'] && a['group'] !== b['group']) {
        return false
      }
      return true
    }
    else if (a['session'] && a['session'] === b['session']) {
        return true
    }
    else if (a === 'mute') {
      return a === b
    }
    else {
      return false
    }
  }
}

// MIDI LEARN facility
export interface IMidiLearn {
  add (learner: TMidiSink)
  remove (learner: TMidiSink)
  learn (learner: TMidiSink)
  state (learner: TMidiSink) : TMidiLearnState
}

/**
 * LISTEN to MIDI input devices
 * @param onNote - callback to observe MIDI note events
 */
export class MidiMonitor {
    access: WebMidi.MIDIAccess | undefined
    ports: TMidiPortRec = {}
    destroyed: boolean = false

    constructor (
      {onNote,ports,onPort}:
      {
        onNote:TMidiNoteObserver
        ports?:string[]  // undefined = all ports, empty = none, array = specific ports
        onPort?: (ports: TMidiPortRec) => void
      })
    {
        // REQUEST MIDI access and MANAGE observance
        // (MacOS WebView (i.e. WebKit) does not support MIDI)
        if (navigator.requestMIDIAccess) {
            navigator.requestMIDIAccess()
            .then(access => {
                if (!this.destroyed) {
                    L('MIDI ENABLED')
                    this.access = access
                    this.observe({access,onNote,portFilter:ports,onPort})
                }
            })
            .catch(e => E('MIDI DISABLED: FAILED to receive access grant',e))
        }
        else {
            W('MIDI DISABLED: UNSUPPORTED (WebKit?)')
        }
    }

    // OBSERVE MIDI events for all current and future devices
    private observe (
      {access,onNote,portFilter,onPort}
      : {
        access:WebMidi.MIDIAccess
        onNote:TMidiNoteObserver
        portFilter?:string[]  // undefined = all ports, empty = none, array = specific ports
        onPort?: (ports: TMidiPortRec) => void
      }
    ) {
      // Helper to check if a port should be observed
      const shouldObserve = (id: string) => !portFilter || portFilter.includes(id)
      const ports = [...access.inputs.values()]
        .reduce((p,input) => {
          p[input.id] = {
            id: input.id,
            name: input.name || input.id,
          }
          return p
        },{})

      this.ports = ports
      setTimeout(() => onPort?.({...this.ports}))
      
      // ATTACH to ports
      access.inputs
        .forEach(input => {
          if (shouldObserve(input.id)) {
            input.onmidimessage = observeMessage
          }
        })

      // OBSERVE device connection events
      access.onstatechange = event => {
        if (event.port.type === 'input') {
          const $port = event.port as WebMidi.MIDIInput
          const {state} = $port
          if (state === 'connected') {
            if (shouldObserve($port.id)) {
              ($port as WebMidi.MIDIInput).onmidimessage = observeMessage
            }
            this.ports[$port.id] = {
              id: $port.id,
              name: $port.name || $port.id,
            }
            L('MIDI IN connect',$port.name)
          }
          else if (state === 'disconnected') {
            delete this.ports[$port.id]
            if (shouldObserve($port.id)) {
              $port.onmidimessage = null
            }
            L('MIDI IN disconnect',$port.name)
          }
          setTimeout(() => onPort?.({...this.ports}))
        }
      }

      // OBSERVE and PROPAGATE incoming MIDI messages (from active port)
      function observeMessage (event: WebMidi.MIDIMessageEvent) {
          const [status, note, velocity] = event.data
          const command = status & 0xF0
          // MIDI CHANNEL (1-16)
          const channel = (event.data[0] & 0xf) + 1
          // VALID NOTES only
          if (note < 0 || note > 127) return

          L('RECEIVED MIDI NOTE',channel,note,velocity)
          // OBSERVE only ON/OFF messages
          if (![0x80,0x90].includes(command)) {
            return
          }
          // OFF?
          // VELOCTY can determine NOTE state if ON command
          const off = command === 0x80 || command === 0x90 && velocity === 0

          const on = !off
          const hard = on && velocity === 127
          const port = event.target ? event.target as MIDIInput : undefined

          const $note = MidiNote.fromNumber({channel,note})
          if (!$note) return
          setTimeout(() => 
            onNote({
                ...$note,
                on: on ? (hard ? 'hard' : true) : undefined,
                off: !on ? (command === 0x90 ? 'synth' : true) : undefined,
                time: new Date().getTime(),
                port: port ? {
                  id: port.id,
                  name: port.name ? port.name : undefined,
                } : undefined,
                velocity,
            }),0)
        }
    }

    live () : boolean {
      return !!this.access
    }

    // CLEANUP
    destroy () {
      this.destroyed = true
      if (this.access) {
        // Remove listeners from all inputs
        this.access.inputs.forEach((input) => {
          input.onmidimessage = null
        })
        // Remove the state change handler
        this.access.onstatechange = null
        this.access = undefined
      }
    }
}
